import { useId, type ReactNode } from 'react';
import { UiLink, useUiLabels } from '../labels';
import { cn } from '../lib/cn';
import { Icon, type IconName } from './icon';
import { Skeleton } from './skeleton';

export type TileSize = 'small' | 'medium' | 'large';

// Columns follow the width of the grid, not of the window: the sidebar takes a different share
// on tablet and desktop, collapsed or not.
const spans: Record<TileSize, string> = {
  small: '',
  medium: '@2xl:col-span-2',
  large: '@2xl:col-span-2 @4xl:col-span-3',
};

/**
 * Grid of tiles: one column on the phone, two from 672 px of content width, three from 896 px.
 * `Tile` sizes span columns of it.
 */
export function TileGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="@container">
      <div className={cn('grid grid-cols-1 gap-4 @2xl:grid-cols-2 @4xl:grid-cols-3', className)}>
        {children}
      </div>
    </div>
  );
}

export interface TileProps {
  title: ReactNode;
  /** Icon in the accent colour, usually the module icon. */
  icon?: IconName;
  /** Page behind the tile. The heading becomes a link to it. */
  href?: string;
  /** Count or status next to the heading. */
  badge?: ReactNode;
  /** Small buttons on the right of the heading. */
  actions?: ReactNode;
  /** Columns in `TileGrid`: one, two or the whole row. Every tile is full width on the phone. */
  size?: TileSize;
  /** Named module accent; icon and accent tokens inside follow it. */
  accent?: string;
  /** Content to the edges, e.g. a `MenuList`. */
  flush?: boolean;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * A tile of a dashboard: heading with icon and link, content below. The start page renders the
 * tiles of the modules in it, overview pages of modules use it in `DashboardPage`.
 */
export function Tile({
  title,
  icon,
  href,
  badge,
  actions,
  size = 'small',
  accent,
  flush = false,
  footer,
  children,
  className,
}: TileProps) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      data-module-accent={accent}
      className={cn(
        'flex min-w-0 flex-col overflow-hidden rounded-lg border border-line bg-surface',
        spans[size],
        className,
      )}
    >
      <header className="flex min-h-12 items-center gap-2.5 px-4 pt-2">
        {icon && (
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-accent-subtle text-accent">
            <Icon name={icon} />
          </span>
        )}
        <h2 id={id} className="min-w-0 flex-1 text-sm font-semibold text-fg">
          {href ? (
            <UiLink
              href={href}
              className="group inline-flex min-h-11 max-w-full items-center gap-1 rounded-sm"
            >
              <span className="truncate group-hover:underline">{title}</span>
              <Icon name="chevron-right" className="text-fg-subtle" />
            </UiLink>
          ) : (
            <span className="block truncate">{title}</span>
          )}
        </h2>
        {badge}
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      </header>
      <div className={cn('flex min-h-0 flex-1 flex-col pt-1 pb-4', !flush && 'px-4')}>
        {children}
      </div>
      {footer && <div className="border-t border-line bg-canvas px-4 py-2.5">{footer}</div>}
    </section>
  );
}

/** Loading state of a tile's content in the shape of a short list. */
export function TileSkeleton({ rows = 3 }: { rows?: number }) {
  const labels = useUiLabels();
  return (
    <div role="status" className="flex flex-col gap-3 py-1">
      <span className="sr-only">{labels.loading}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex flex-col gap-1.5">
          <Skeleton className={cn('h-4', index % 2 === 0 ? 'w-3/4' : 'w-2/3')} />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}
